'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import {
  buildSpmPositionCode,
  padSpmPositionIndex,
  useGlobalSpmTerminal,
} from '@/components/Layout/spmTerminal';
import dynamic from 'next/dynamic';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { withSpmCnt } from '@/reporting/spm';
import { EPlatform } from '@/utils/enums/platform-enum';

// 预加载 Tabs，使用 ssr: true 避免加载延迟
const SideNavTabs = dynamic(() => import('./SideNavTabs.pc'), { ssr: true });
const SideNavDropdownSSR = dynamic(() => import('./SideNavDropdown.pc'), { ssr: true });

interface SideNavWrapperProps {
  navData: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  isDarkMode: boolean;
  isBot: boolean;
}

const SideNavWrapper: FC<SideNavWrapperProps> = ({ navData, isDarkMode }) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  // PC 大屏顶部导航仅在 WebOnly 分支渲染；全局组件 B 位固定 global_pc。
  const spmPage = useGlobalSpmTerminal(EPlatform.WEB);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasActiveDropdown = showDropdown && Boolean(navData[activeTabIndex]?.children?.length);

  useEffect(() => {
    const checkWidth = () => {
      setShouldRender(window.innerWidth > 1280);
    };
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => {
      window.removeEventListener('resize', checkWidth);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTabMouseEnter = useCallback(
    (index: number) => {
      clearHideTimeout();
      if (index === activeTabIndex) return;
      setActiveTabIndex(index);
      setShowDropdown(true);
      const newActiveNavItem = navData[index];
      const hasChildren = newActiveNavItem?.children?.length > 0;
      if (hasChildren) {
        // 面板曝光挂点=展开的一级 tab，坑位与该 tab 点击/DOM 打标同源（nav_item_XX）。
        const navItemSpmCnt = buildSpmPositionCode(
          spmPage,
          'primary_navigation',
          `nav_item_${padSpmPositionIndex(index + 1)}`,
        );
        trackEvent(
          {
            eventName: 'TopNavigation_Exp',
            properties: withSpmCnt(
              {
                page_name: cnName,
                url_path: window.location.href,
                tab_name: newActiveNavItem.content?.nav_name,
                tab_index: index,
              },
              navItemSpmCnt,
            ),
          },
          [ProviderType.SENSORS],
        );
      }
    },
    [activeTabIndex, navData, cnName, spmPage, trackEvent],
  );

  const handleTabMouseLeave = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setShowDropdown(false);
      setActiveTabIndex(-1);
    }, 100);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    clearHideTimeout();
    setShowDropdown(true);
  }, []);

  const handleDropdownMouseLeave = useCallback(() => {
    clearHideTimeout();
    setShowDropdown(false);
    setActiveTabIndex(-1);
  }, []);

  const handleSubnodeClick = useCallback(() => {
    setShowDropdown(false);
  }, []);

  if (!shouldRender) return null;

  return (
    <>
      {/* Tabs: 统一使用 SideNavTabs，它内部处理水合状态 */}
      <SideNavTabs
        navData={navData}
        isDarkMode={isDarkMode}
        activeTabIndex={activeTabIndex}
        onTabMouseEnter={handleTabMouseEnter}
        onTabMouseLeave={handleTabMouseLeave}
        containerRef={containerRef}
        onSubnodeClick={handleSubnodeClick}
      />
      {hasActiveDropdown ? (
        <div
          className={`fixed inset-x-0 bottom-0 z-[201] bg-black/20 ${isDarkMode ? 'top-[60px]' : 'top-[100px]'}`}
          data-testid="desktop-navigation-backdrop"
          aria-hidden
        />
      ) : null}
      {/* Dropdown: 默认SSR，保证二/三级菜单链接进入初始HTML */}
      <SideNavDropdownSSR
        navData={navData}
        isDarkMode={isDarkMode}
        activeTabIndex={activeTabIndex}
        showDropdown={showDropdown}
        onDropdownMouseEnter={handleDropdownMouseEnter}
        onDropdownMouseLeave={handleDropdownMouseLeave}
      />
    </>
  );
};

export default SideNavWrapper;
